import { Drawer } from '@/components';
import { useProject } from '@/features/project/_hooks/project';
import type { CurrentFinalizedAmountFormSchema } from '@/features/project/projects/_models/segment-expenditure-and-execution';
import {
  useCreateSegmentExpenditureAndExecution,
  useUpdateSegmentExpenditureAndExecution,
} from '@/features/project/projects/detail/income-and-expenditure/_hooks/segment-expenditure-and-execution';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../../_components/ProjectDrawerStateProvider';
import { CurrentFinalizedAmountForm } from './CurrentFinalizedAmountForm';

export const CurrentFinalizedAmountFormDrawer: FC = () => {
  const { t } = useTranslation();
  const { id: projectId } = useId();

  const project = useProject(projectId);

  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const { openMessage } = useMessage();
  const isOpenDraw =
    drawerState.open && drawerState.type === 'ss-current-finalized-amount';
  const handleClose = async () => {
    setDrawerState({ open: false });
  };

  const { mutateAsync: createMutateAsync } =
    useCreateSegmentExpenditureAndExecution();
  const { mutateAsync: updateMutateAsync } =
    useUpdateSegmentExpenditureAndExecution();

  const initialSegmentExpenditureAndExecution =
    project.data?.segmentExpenditureAndExecution;

  return (
    <Drawer open={isOpenDraw} onClose={handleClose} destroyOnClose>
      <CurrentFinalizedAmountForm
        initialValues={
          initialSegmentExpenditureAndExecution
            ? {
                ...initialSegmentExpenditureAndExecution,
                revenueManagementExpenseSubsidyMonetizationStandards:
                  initialSegmentExpenditureAndExecution.revenueManagementExpenseSubsidyMonetizationStandards ??
                  [],
              }
            : undefined
        }
        onSubmit={async (
          values: z.infer<typeof CurrentFinalizedAmountFormSchema>,
        ) => {
          if (initialSegmentExpenditureAndExecution) {
            await updateMutateAsync({
              params: {
                ...values,
                projectId,
                id: initialSegmentExpenditureAndExecution.id,
              },
            });
          } else {
            await createMutateAsync({
              params: { ...values, projectId },
            });
          }
          await Promise.all([
            openMessage('success', t('message-update-completed')),
            handleClose(),
          ]);
        }}
      />
    </Drawer>
  );
};
